import { useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useCsvExport } from '../../hooks/useCsvExport';
import { HelpTip } from '../../components/HelpTip';
import { ETIQUETA_SIN_MOTIVO, SENTIDO_LABEL, etiquetaACargoDe, type SentidoMotivo } from '../../lib/motivosMovimiento';
// Pesos con centavos: el mismo formato que los costos de Formación de Precios.
import { fmtCosto as fmtPesos } from '../formacion-precios/formato';
import { aLocalAr } from '../../lib/fechaHoraAr';

interface FilaInforme {
  clave: string;
  motivoId: number | null;
  codigo: string | null;
  nombre: string;
  sentido: SentidoMotivo;
  aCargoDe: string | null;
  movimientos: number;
  unidades: number;
  valor: number;
  skusSinCosto: number;
}
interface Acumulado { unidades: number; valor: number }
interface TotalACargoDe { aCargoDe: string | null; salidas: Acumulado; entradas: Acumulado; neto: number }
interface Informe {
  valuacion: string;
  filas: FilaInforme[];
  porACargoDe: TotalACargoDe[];
  totales: { salidas: Acumulado; entradas: Acumulado; neto: number };
}

const fmtUnidades = (n: number) => n.toLocaleString('es-AR', { maximumFractionDigits: 4 });

/** Hoy y el 1.° del mes en curso (días de Argentina), AAAA-MM-DD. */
function mesEnCurso(): { desde: string; hasta: string } {
  const hoy = aLocalAr(new Date()).slice(0, 10);
  return { desde: `${hoy.slice(0, 8)}01`, hasta: hoy };
}

const COLUMNAS_CSV = [
  { key: 'nombre', header: 'Motivo' },
  { key: 'sentidoTexto', header: 'Sentido' },
  { key: 'aCargoDeTexto', header: 'A cargo de' },
  { key: 'movimientos', header: 'Movimientos' },
  { key: 'unidades', header: 'Unidades' },
  { key: 'valor', header: 'Valor (costo promedio actual)' },
  { key: 'skusSinCosto', header: 'SKU sin costo' },
];

/**
 * "Bajas y altas por motivo": movimientos manuales del rango agrupados por motivo del catálogo (los que no tienen, en
 * ETIQUETA_SIN_MOTIVO, por sentido), con unidades y valor al costo promedio actual del catálogo, y totales por "a cargo de".
 */
export function BajasAltasPorMotivo() {
  const inicial = useMemo(mesEnCurso, []);
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const endpoint = desde && hasta
    ? `/api/v3/movimientos-stock/informe-motivos?desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}`
    : null;
  const { data, loading, error } = useApi<Informe>(endpoint);
  const filas = Array.isArray(data?.filas) ? data!.filas : [];
  const porACargoDe = Array.isArray(data?.porACargoDe) ? data!.porACargoDe : [];

  const filasCsv = useMemo(() => filas.map(f => ({
    ...f, sentidoTexto: SENTIDO_LABEL[f.sentido] ?? f.sentido, aCargoDeTexto: etiquetaACargoDe(f.aCargoDe),
  })), [filas]);
  const { exportCsv } = useCsvExport(COLUMNAS_CSV, filasCsv, `Bajas-y-altas-por-motivo-${desde}-a-${hasta}`);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-800 inline">Bajas y altas por motivo</h2>
        <HelpTip
          tips={[
            'Bajas, ajustes y devoluciones cargados a mano (y los ajustes de los conteos), agrupados por motivo del catálogo.',
            `"${ETIQUETA_SIN_MOTIVO}": bajas y ajustes manuales registrados antes de que existiera el catálogo de motivos (hoy el motivo es obligatorio). No se reescriben: se muestran así.`,
            'El valor es unidades × costo promedio ACTUAL del SKU en el catálogo, no el costo que tenía cuando pasó.',
          ]}
        />
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="informe-desde" className="block text-xs font-medium text-slate-600 mb-1">Desde</label>
          <input id="informe-desde" type="date" className="border border-slate-300 rounded-md px-3 py-2 text-sm" value={desde} onChange={e => setDesde(e.target.value)} />
        </div>
        <div>
          <label htmlFor="informe-hasta" className="block text-xs font-medium text-slate-600 mb-1">Hasta</label>
          <input id="informe-hasta" type="date" className="border border-slate-300 rounded-md px-3 py-2 text-sm" value={hasta} onChange={e => setHasta(e.target.value)} />
        </div>
        <p className="text-xs text-slate-500 pb-2">Días de Argentina, por la fecha del hecho; hasta un año.</p>
        <button
          type="button"
          onClick={exportCsv}
          disabled={filas.length === 0}
          className="ml-auto px-3 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
          title="Exportar a CSV"
        >
          Exportar CSV
        </button>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}
      {data?.valuacion && <p className="text-xs text-slate-500">{data.valuacion}.</p>}

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" aria-label="Por motivo">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Motivo</th>
              <th className="px-3 py-2">Sentido</th>
              <th className="px-3 py-2">A cargo de</th>
              <th className="px-3 py-2 text-right">Movimientos</th>
              <th className="px-3 py-2 text-right">Unidades</th>
              <th className="px-3 py-2 text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {loading && filas.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-slate-500">Cargando…</td></tr>}
            {!loading && filas.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-slate-500">Sin bajas ni altas manuales en el rango</td></tr>}
            {filas.map(f => (
              <tr key={f.clave} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  {f.nombre}
                  {f.skusSinCosto > 0 && (
                    <span className="block text-xs text-amber-700">{f.skusSinCosto} SKU sin costo (suman $0)</span>
                  )}
                </td>
                <td className="px-3 py-2">{SENTIDO_LABEL[f.sentido] ?? f.sentido}</td>
                <td className="px-3 py-2">{etiquetaACargoDe(f.aCargoDe)}</td>
                <td className="px-3 py-2 text-right">{f.movimientos}</td>
                <td className={`px-3 py-2 text-right ${f.sentido === 'salida' ? 'text-red-700' : 'text-green-700'}`}>
                  {f.sentido === 'salida' ? '−' : '+'}{fmtUnidades(f.unidades)}
                </td>
                <td className="px-3 py-2 text-right">{fmtPesos(f.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto max-w-3xl">
        <table className="w-full text-sm" aria-label="Totales por a cargo de">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">A cargo de</th>
              <th className="px-3 py-2 text-right">Salidas</th>
              <th className="px-3 py-2 text-right">Entradas</th>
              <th className="px-3 py-2 text-right">Neto</th>
            </tr>
          </thead>
          <tbody>
            {porACargoDe.map(r => (
              <tr key={r.aCargoDe ?? 'sin-asignar'} className="border-t border-slate-100">
                <td className="px-3 py-2">{etiquetaACargoDe(r.aCargoDe)}</td>
                <td className="px-3 py-2 text-right">{fmtPesos(r.salidas.valor)}</td>
                <td className="px-3 py-2 text-right">{fmtPesos(r.entradas.valor)}</td>
                <td className={`px-3 py-2 text-right ${r.neto < 0 ? 'text-red-700' : ''}`}>{fmtPesos(r.neto)}</td>
              </tr>
            ))}
            {data?.totales?.salidas && data.totales.entradas && (
              <tr className="border-t-2 border-slate-200 font-semibold">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{fmtPesos(data.totales.salidas.valor)}</td>
                <td className="px-3 py-2 text-right">{fmtPesos(data.totales.entradas.valor)}</td>
                <td className={`px-3 py-2 text-right ${data.totales.neto < 0 ? 'text-red-700' : ''}`}>{fmtPesos(data.totales.neto)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
